import React, { useState, useEffect } from 'react';
import { formatBookName, OLD_TESTAMENT, NEW_TESTAMENT } from '../../utils/bibleBooks';
import { CaretDown } from '@phosphor-icons/react';
import { bibleService } from '../../../../shared/services/bibleService';
import { Menu } from '@headlessui/react';

interface BibleNavigationProps {
  selectedBook: string;
  selectedChapter: string;
  onNavigate: (book: string, chapter: string) => void;
}

export function BibleNavigation({ selectedBook, selectedChapter, onNavigate }: BibleNavigationProps) {
  const [chapterCount, setChapterCount] = useState(0);
  const [currentBookName, setCurrentBookName] = useState('');

  // Get book name when selected book changes
  useEffect(() => {
    const loadBookInfo = async () => {
      const { name } = await bibleService.getBookInfo(selectedBook);
      setCurrentBookName(name);
      const count = await bibleService.getChapterCount(selectedBook);
      setChapterCount(count);
    };
    loadBookInfo();
  }, [selectedBook]);

  // Combine all books into a single array
  const allBooks = [
    ...Object.values(OLD_TESTAMENT).flat(),
    ...Object.values(NEW_TESTAMENT).flat()
  ];

  return (
    <div className="flex items-center justify-center gap-4 py-4">
      <Menu as="div" className="relative">
        <Menu.Button className="flex items-center gap-2 px-4 py-2 bg-[#F5EEE8] rounded-lg hover:bg-[#EBE2DA] transition-colors">
          <span className="text-[#361E06] font-medium">{currentBookName}</span>
          <CaretDown className="w-4 h-4 text-[#361E06]" weight="bold" />
        </Menu.Button>
        <Menu.Items className="absolute z-50 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 max-h-[300px] overflow-y-auto">
          {allBooks.map((book) => (
            <Menu.Item key={book}>
              {({ active }) => (
                <button
                  onClick={async () => {
                    const bookInfo = await bibleService.getBookInfo(book);
                    onNavigate(bookInfo.number, '1');
                  }}
                  className={`${
                    active ? 'bg-gray-50' : ''
                  } w-full text-left px-4 py-2 text-[#361E06]`}
                >
                  {formatBookName(book)}
                </button>
              )}
            </Menu.Item>
          ))}
        </Menu.Items>
      </Menu>

      <Menu as="div" className="relative">
        <Menu.Button className="flex items-center gap-2 px-4 py-2 bg-[#F5EEE8] rounded-lg hover:bg-[#EBE2DA] transition-colors">
          <span className="text-[#361E06] font-medium">Chapter {selectedChapter}</span>
          <CaretDown className="w-4 h-4 text-[#361E06]" weight="bold" />
        </Menu.Button>
        <Menu.Items className="absolute z-50 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 max-h-[300px] overflow-y-auto">
          <div className="grid grid-cols-4 gap-1 p-2">
            {Array.from({ length: chapterCount }, (_, i) => (
              <Menu.Item key={i + 1}>
                {({ active }) => (
                  <button
                    onClick={() => onNavigate(selectedBook, String(i + 1))}
                    className={`${
                      active ? 'bg-gray-50' : ''
                    } w-full p-2 text-center rounded-lg text-[#361E06] hover:bg-gray-50`}
                  >
                    {i + 1}
                  </button>
                )}
              </Menu.Item>
            ))}
          </div>
        </Menu.Items>
      </Menu>
    </div>
  );
}